/**
 * HierarchyTableV2 Component
 *
 * Displays hierarchical SPLH data with comparison metrics for the SPLH Performance Dashboard V2.
 * Uses TanStack Table v8 with standard HTML table elements (NOT MUI Table components).
 *
 * Features:
 * - TanStack Table v8 with HTML elements
 * - Column sorting (client-side)
 * - Row click for drilldown navigation
 * - Comparison-aware metrics with trend arrows and delta percentages
 * - Color-coded trend indicators
 * - Comparison tooltips showing comparison value and delta
 * - Loading state with skeleton rows
 * - Dynamic column headers from hierarchyMetadata
 *
 * Columns (per SPLH Performance Intelligence spec):
 * - Name (hierarchy level)
 * - SPLH (primary metric, first)
 * - Sales
 * - Labor Hours
 * - Labor Dollars
 * - Labor %
 * - Check Count
 *
 * @module pages/common/dashboards/SPLHDashboard/components/HierarchyTableV2
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Box, Typography, Skeleton, Tooltip } from '@mui/material';
import {
  ChevronRight as ExpandIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { formatSPLH, formatCurrency, formatNumber, formatPercentage } from '../../utils/formatUtils';

/**
 * Sort indicator component
 */
const SortIndicator = ({ sorted }) => {
  if (!sorted) return null;
  return (
    <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>
      {sorted === 'asc' ? '▲' : '▼'}
    </span>
  );
};

/**
 * Loading skeleton row component
 */
const SkeletonRow = ({ columnCount }) => (
  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
    {Array.from({ length: columnCount }).map((_, index) => (
      <td key={index} style={{ padding: '12px 16px' }}>
        <Skeleton variant="text" width={index === 0 ? '80%' : '60%'} height={24} />
      </td>
    ))}
  </tr>
);

/**
 * Trend indicator component for V2 metrics
 * @param {Object} props - Component props
 * @param {string} props.direction - Trend direction: 'UP', 'DOWN', or 'FLAT'
 * @param {number} props.deltaPct - Delta percentage value
 * @param {Object} props.colors - Color configuration with positive, negative, neutral
 * @param {boolean} [props.neutralOnly=false] - If true, always use neutral color (for hours)
 */
const TrendIndicator = ({ direction, deltaPct, colors, neutralOnly = false }) => {
  let Icon;
  let color;

  switch (direction?.toUpperCase()) {
    case 'UP':
      Icon = TrendingUpIcon;
      color = neutralOnly ? colors.neutral : colors.positive;
      break;
    case 'DOWN':
      Icon = TrendingDownIcon;
      color = neutralOnly ? colors.neutral : colors.negative;
      break;
    default:
      Icon = TrendingFlatIcon;
      color = colors.neutral;
  }

  const sign = deltaPct >= 0 ? '+' : '';
  const deltaText = deltaPct !== null && deltaPct !== undefined ? `${sign}${deltaPct.toFixed(1)}%` : '';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
      <Icon sx={{ fontSize: 16, color }} />
      {deltaText && (
        <Typography variant="caption" sx={{ color, fontWeight: 500, fontSize: '0.7rem' }}>
          {deltaText}
        </Typography>
      )}
    </Box>
  );
};

TrendIndicator.propTypes = {
  direction: PropTypes.string,
  deltaPct: PropTypes.number,
  colors: PropTypes.object.isRequired,
  neutralOnly: PropTypes.bool,
};

/**
 * Builds tooltip content for comparison values
 * @param {Object} metricValue - The metric value object with comparison data
 * @param {Function} formatter - Formatter function for the comparison value
 * @returns {string} Tooltip content string
 */
const buildTooltipContent = (metricValue, formatter) => {
  if (!metricValue?.comparison) return '';

  const comparisonFormatted = formatter(metricValue.comparison);
  const sign = metricValue.deltaPct >= 0 ? '+' : '';
  const deltaFormatted = metricValue.deltaPct !== null && metricValue.deltaPct !== undefined
    ? `${sign}${metricValue.deltaPct.toFixed(1)}%`
    : '';

  return `Comparison: ${comparisonFormatted}${deltaFormatted ? ` | Δ: ${deltaFormatted}` : ''}`;
};

/**
 * Metric cell component that displays current value with trend indicator and comparison tooltip
 * @param {Object} props - Component props
 * @param {Object} props.metricValue - Metric value object with current, comparison, delta, deltaPct, direction
 * @param {Function} props.formatter - Formatter function for displaying values
 * @param {Object} props.colors - Color configuration
 * @param {boolean} [props.showTrend=true] - Whether to show trend indicator
 * @param {boolean} [props.neutralOnly=false] - If true, trend arrows are always gray (for hours)
 */
const MetricCell = ({ metricValue, formatter, colors, showTrend = true, neutralOnly = false }) => {
  if (!metricValue) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  const { current, comparison, direction, deltaPct } = metricValue;
  const formattedValue = formatter(current);
  const tooltipContent = buildTooltipContent(metricValue, formatter);

  const cellContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="body2">{formattedValue}</Typography>
      {showTrend && direction && (
        <TrendIndicator
          direction={direction}
          deltaPct={deltaPct}
          colors={colors}
          neutralOnly={neutralOnly}
        />
      )}
    </Box>
  );

  // Wrap in tooltip if comparison data is available
  if (comparison !== null && comparison !== undefined) {
    return (
      <Tooltip title={tooltipContent} placement="top" arrow>
        {cellContent}
      </Tooltip>
    );
  }

  return cellContent;
};

MetricCell.propTypes = {
  metricValue: PropTypes.shape({
    current: PropTypes.number,
    comparison: PropTypes.number,
    delta: PropTypes.number,
    deltaPct: PropTypes.number,
    direction: PropTypes.string,
  }),
  formatter: PropTypes.func.isRequired,
  colors: PropTypes.object.isRequired,
  showTrend: PropTypes.bool,
  neutralOnly: PropTypes.bool,
};

/**
 * HierarchyTableV2 Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.data - Hierarchy nodes from backend V2 API
 * @param {Object} props.hierarchyMetadata - Metadata with level info
 * @param {string} props.currentLevel - Current hierarchy level being displayed
 * @param {Function} props.onDrilldown - Callback when row is clicked for drilldown
 * @param {boolean} [props.loading=false] - Whether data is loading
 * @param {Object} [props.colors] - Color configuration
 * @returns {React.Component} HierarchyTableV2 component
 */
const HierarchyTableV2 = ({
  data = [],
  hierarchyMetadata,
  currentLevel,
  onDrilldown,
  loading = false,
  colors,
}) => {
  // Default colors
  const chartColors = colors || {
    positive: '#10B981',
    negative: '#EF4444',
    neutral: '#6B7280',
  };

  // Get the display name for the current level
  const levelDisplayName = useMemo(() => {
    if (!hierarchyMetadata?.levels || !currentLevel) {
      return 'Location';
    }
    const levelInfo = hierarchyMetadata.levels.find((l) => l.level === currentLevel);
    return levelInfo?.displayName || 'Location';
  }, [hierarchyMetadata, currentLevel]);

  // Define table columns with V2 MetricValue structure
  // Column order per SPLH Performance Intelligence spec:
  // Name → SPLH → Sales → Labor Hours → Labor Dollars → Labor % → Check Count
  const columns = useMemo(() => [
    // 1. Name column (hierarchy level)
    {
      accessorKey: 'name',
      header: levelDisplayName,
      cell: ({ row, getValue }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {row.original.hasChildren ? (
            <ExpandIcon
              sx={{
                fontSize: 20,
                color: 'text.secondary',
                transition: 'transform 0.2s ease',
              }}
            />
          ) : (
            <Box sx={{ width: 20 }} />
          )}
          <Typography variant="body2" fontWeight={500}>
            {getValue()}
          </Typography>
        </Box>
      ),
      meta: { align: 'left' },
    },
    // 2. SPLH - Primary metric, first data column
    {
      id: 'splh',
      accessorFn: (row) => row.splh?.current,
      header: 'SPLH',
      cell: ({ row }) => (
        <MetricCell
          metricValue={row.original.splh}
          formatter={formatSPLH}
          colors={chartColors}
        />
      ),
      meta: { align: 'center' },
    },
    // 3. Sales
    {
      id: 'sales',
      accessorFn: (row) => row.sales?.current,
      header: 'Sales',
      cell: ({ row }) => (
        <MetricCell
          metricValue={row.original.sales}
          formatter={(v) => formatCurrency(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          colors={chartColors}
        />
      ),
      meta: { align: 'center' },
    },
    // 4. Check Count - Higher is better (more customers)
    {
      id: 'checkCount',
      accessorFn: (row) => row.checkCount?.current,
      header: 'Checks',
      cell: ({ row }) => (
        <MetricCell
          metricValue={row.original.checkCount}
          formatter={(v) => formatNumber(v, { decimalPlaces: 0 })}
          colors={chartColors}
        />
      ),
      meta: { align: 'center' },
    },
    // 5. Labor Hours - Shows gray trend arrows (neutral only)
    {
      id: 'laborHours',
      accessorFn: (row) => row.laborHours?.current,
      header: 'Labor Hours',
      cell: ({ row }) => (
        <MetricCell
          metricValue={row.original.laborHours}
          formatter={(v) => formatNumber(v, { decimalPlaces: 1 })}
          colors={chartColors}
          showTrend={true}
          neutralOnly={true}
        />
      ),
      meta: { align: 'center' },
    },
    // 5. Labor Dollars - Higher is worse, so invert colors
    {
      id: 'laborDollars',
      accessorFn: (row) => row.laborDollars?.current,
      header: 'Labor $',
      cell: ({ row }) => (
        <MetricCell
          metricValue={row.original.laborDollars}
          formatter={(v) => formatCurrency(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          colors={{
            // Inverted colors for labor dollars: down is good, up is bad
            positive: chartColors.negative,
            negative: chartColors.positive,
            neutral: chartColors.neutral,
          }}
        />
      ),
      meta: { align: 'center' },
    },
    // 7. Labor % - Higher is worse, so invert colors
    {
      id: 'laborPct',
      accessorFn: (row) => row.laborPct?.current,
      header: 'Labor %',
      cell: ({ row }) => (
        <MetricCell
          metricValue={row.original.laborPct}
          formatter={(v) => formatPercentage(v / 100, { decimalPlaces: 1 })}
          colors={{
            // Inverted colors for labor %: down is good, up is bad
            positive: chartColors.negative,
            negative: chartColors.positive,
            neutral: chartColors.neutral,
          }}
        />
      ),
      meta: { align: 'center' },
    },
  ], [levelDisplayName, chartColors]);

  // Initialize TanStack Table
  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'splh', desc: true }],
    },
  });

  // Handle row click
  const handleRowClick = (row) => {
    if (row.original.hasChildren && onDrilldown) {
      onDrilldown(row.original);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              {columns.map((column, index) => (
                <th
                  key={index}
                  style={{
                    padding: '12px 16px',
                    textAlign: column.meta?.align || 'left',
                    fontWeight: 600,
                    color: '#424242',
                    backgroundColor: '#fafafa',
                  }}
                >
                  {typeof column.header === 'string' ? column.header : 'Column'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonRow key={index} columnCount={columns.length} />
            ))}
          </tbody>
        </table>
      </Box>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          bgcolor: '#fafafa',
          borderRadius: 1,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No hierarchy data available
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Data will appear when available for the selected date range
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} style={{ borderBottom: '2px solid #e0e0e0' }}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    padding: '12px 16px',
                    textAlign: header.column.columnDef.meta?.align || 'left',
                    fontWeight: 600,
                    color: '#424242',
                    backgroundColor: '#fafafa',
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: header.column.columnDef.meta?.align === 'center'
                        ? 'center'
                        : header.column.columnDef.meta?.align === 'right'
                          ? 'flex-end'
                          : 'flex-start',
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <SortIndicator sorted={header.column.getIsSorted()} />
                    )}
                  </Box>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => handleRowClick(row)}
              style={{
                borderBottom: '1px solid #e0e0e0',
                cursor: row.original.hasChildren ? 'pointer' : 'default',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (row.original.hasChildren) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={{
                    padding: '12px 16px',
                    textAlign: cell.column.columnDef.meta?.align || 'left',
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

/**
 * Shape for MetricValue objects from API
 */
const MetricValueShape = PropTypes.shape({
  current: PropTypes.number,
  comparison: PropTypes.number,
  delta: PropTypes.number,
  deltaPct: PropTypes.number,
  direction: PropTypes.string,
});

HierarchyTableV2.propTypes = {
  /** Hierarchy nodes from backend V2 API */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      level: PropTypes.string,
      parentId: PropTypes.string,
      // All 6 key metrics with comparison support
      splh: MetricValueShape,
      sales: MetricValueShape,
      laborHours: MetricValueShape,
      laborDollars: MetricValueShape,
      laborPct: MetricValueShape,
      checkCount: MetricValueShape,
      // FOH/BOH breakdown (optional)
      fohHours: PropTypes.number,
      bohHours: PropTypes.number,
      hasChildren: PropTypes.bool,
    })
  ),
  /** Metadata with level information */
  hierarchyMetadata: PropTypes.shape({
    levels: PropTypes.arrayOf(
      PropTypes.shape({
        level: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        order: PropTypes.number,
        isUsed: PropTypes.bool,
      })
    ),
    clientName: PropTypes.string,
  }),
  /** Current hierarchy level being displayed */
  currentLevel: PropTypes.string,
  /** Callback when row is clicked for drilldown */
  onDrilldown: PropTypes.func,
  /** Whether data is loading */
  loading: PropTypes.bool,
  /** Color configuration */
  colors: PropTypes.shape({
    positive: PropTypes.string,
    negative: PropTypes.string,
    neutral: PropTypes.string,
  }),
};

export default HierarchyTableV2;
